import { Component, Vue, Prop } from 'vue-property-decorator';
import {
  IModalComponent, FormButtonDisabler, DropdownField, Field, TextField, DateField, NumberField, Btn, Action, downloadBlob,
  Checkbox, DocumentService, DataImportService
} from '@samc/centricity-core';
import { LsDomainFieldSource, FieldEnumSource } from '@/Sources';
import { ILsDomainField } from '@/Interfaces';
import XLSX from '@samc/sheetjs-style';
import { AjaxHelper, errToMessage, getToken, Guid } from '@samc/common';
import { gotoPost } from '@/utils';

type InputType = '' | 'enum' | 'date' | 'number' | 'text'

interface ISelectedField {
  field: ILsDomainField | null;
  value: unknown;
  source: FieldEnumSource | null;
  inputType: InputType | null;
}

@Component({
  name: 'override-internal-data-modal',
  components: { FormButtonDisabler, DropdownField, Field, TextField, DateField, NumberField, Btn, Action, Checkbox },
})
export default class OverrideInternalDataModal extends Vue implements IModalComponent {
  public documentService: DocumentService | null = null;
  public dataImportService: DataImportService | null = null;
  public ajaxHelper: AjaxHelper | null = null;

  @Prop() public addCloseProp!: (callback: () => Promise<undefined>) => void;
  @Prop() public closeProp!: () => void;
  @Prop() public loans!: number[];
  @Prop() public url!: string;
  @Prop({ default: () => null }) public dataImportUrl!: string | null;
  @Prop({ default: () => null }) public documentUrl!: string | null;
  @Prop() public verificationToken!: string;
  @Prop() public user!: string;

  public fieldSource: LsDomainFieldSource | ILsDomainField[] = [];
  public selectedFields: ISelectedField[] = [];
  public keepNull: boolean = false;
  public saving: boolean = false;

  public async mounted() {
    this.fieldSource = new LsDomainFieldSource();
    this.documentService = new DocumentService(new AjaxHelper(this.documentUrl ?? this.url, this.verificationToken, null, getToken()));
    this.dataImportService = new DataImportService(new AjaxHelper(this.dataImportUrl ?? this.url, this.verificationToken, null, getToken()));
    this.ajaxHelper = new AjaxHelper(this.url, this.verificationToken, null, getToken());

    /// Add the first empty field
    this.addEmptyField();
  }

  public setField(item: ISelectedField, field: ILsDomainField | null) {
    const currType = this.getInputType(item.field);
    const newType = this.getInputType(field);

    item.field = field;
    console.log(field);
    item.source = field?.isEnum === true ? new FieldEnumSource(field.internalFieldId) : null;
    item.inputType = newType;
    if (newType === 'enum' || currType !== newType)
      item.value = null;
  }

  public getInputType(field: ILsDomainField | null) {
    if (field === null) return '';
    if (field.isEnum) return 'enum';

    const dataType = field.dataType?.toLowerCase() ?? '';
    if (dataType.includes('date'))
      return 'date';
    else if (dataType.includes('decimal') || dataType.includes('int'))
      return 'number';
    return 'text';
  }



  public downloadTemplate() {
    try {
      const file = this._generateFile();
      downloadBlob(file, 'InternalData.xlsx');
    } catch (e) {
      this.$alert.error('An error occurred while generating the file');
    }
  }

  public async updateInternalData() {
    if (this.documentService === null || this.dataImportService === null || this.ajaxHelper === null)
      return;

    try {
      this.saving = true;

      this.$loading('Generating File...');
      const file = this._generateFile();

      this.$loading('Uploading File...');
      const mimetype = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const document = await this.documentService.createDocument(file, Guid.create().toString(), mimetype, 'Simple Internal Data Import Template');

      this.$loading('Extracting Data From File...');
      const result = await this.dataImportService.addExcelFileRawData(
        document.documentId, 0, '', document.fileName, this.user, 1, 2, 1, 1, { importProcessId: '19' }
      );

      this.$loading('Importing Template...');
      const request = { sourceId: result.file.id, metaId: result.rawMetaData.id, user: this.user, keepNull: this.keepNull };
      const response = await this.ajaxHelper.postData<number>('internal/data/template/import', request);

      if (this.keepNull) {
        const shouldGo = await this.$confirm({
          type: 'success',
          title: 'Template imported successfully',
          description: 'Would you like to go to survivorship?',
          button: {
            text: 'Go To Targeted Survivorship', data: true
          }
        });
        if (shouldGo === true)
          await this.gotoSurvivorship(result.file.id);
      }

      this.$alert.success(`Template imported successfully with ${response} rows updated.`);
      this.closeProp();
    } catch (err) {
      this.$log(err);
      this.$alert({
        type: 'error',
        title: 'Failed to import template',
        description: 'For more detailed information, use the advanced internal data import',
        errors: errToMessage(err),
        closingOption: 'holdOpen'
      });
    } finally {
      this.saving = false;
      this.$loading();
    }
  }

  public async gotoSurvivorship(sourceId: string) {
    gotoPost('/DataImport/Survivorship', this.verificationToken, { sourceId, goldenIsNullOption: 'highestNonNull', goldenIsNotNullOption: 'highestNonNull' });
  }

  public removeField(field: ISelectedField) {
    const index = this.selectedFields.indexOf(field);
    if (index >= 0)
      this.selectedFields.splice(index, 1);
  }

  public addEmptyField() {
    this.selectedFields.push({ field: null, value: null, source: null, inputType: null });
  }

  private _generateFile(): File {
    const data = [
      ['LoanId', ...this.selectedFields.map(f => f.field?.displayName ?? '')],
      ...this.loans.map(l => [l, ...this.selectedFields.map(f => f.value)])
    ];

    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Internal Data');
    const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    return new File([buffer], 'InternalData.xlsx', { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  }
}